import React from 'react';
import { ShieldCheckIcon, AlertTriangleIcon, DownloadIcon, UsersIcon, CheckCircleIcon, ListChecksIcon, SparklesIcon, ClockIcon, PlusCircleIcon, CheckSquareIcon, UserIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
const typeConfig = {
  hazard: {
    icon: AlertTriangleIcon,
    color: 'text-red-500',
    border: 'border-l-red-500',
    label: 'New Hazard'
  },
  control: {
    icon: ShieldCheckIcon,
    color: 'text-green-500',
    border: 'border-l-green-500',
    label: 'New Control'
  },
  check: {
    icon: CheckSquareIcon,
    color: 'text-blue-500',
    border: 'border-l-blue-500',
    label: 'Safety Check'
  }
};
export function Screen16_UpdatedPTP({
  data,
  onNext,
  onBack,
  onExport
}: ScreenProps) {
  return <div className="flex flex-col h-full" data-id="element-1099">
      <div className="flex items-center gap-3 mb-4" data-id="element-1100">
        <ShieldCheckIcon className="w-8 h-8 text-yellow-500" data-id="element-1101" />
        <h2 className="text-2xl font-bold text-gray-900 leading-tight" data-id="element-1102">
          PTP Updated from Meeting
        </h2>
      </div>

      <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-600 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6 self-start" data-id="element-1103">
        <SparklesIcon className="w-4 h-4" data-id="element-1104" />
        Updated by AI Review
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-4 px-4 space-y-6" data-id="element-1105">
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-id="element-1106">
          <div className="grid grid-cols-2 gap-4" data-id="element-1107">
            <div data-id="element-1108">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1" data-id="element-1109">
                Project
              </p>
              <p className="text-gray-900 font-medium text-sm" data-id="element-1110">
                {data.project}
              </p>
            </div>
            <div data-id="element-1111">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1" data-id="element-1112">
                Location
              </p>
              <p className="text-gray-900 font-medium text-sm" data-id="element-1113">
                {data.location}
              </p>
            </div>
          </div>
          <div className="pt-3 border-t border-gray-200" data-id="element-1114">
            <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1" data-id="element-1115">
              Task
            </p>
            <p className="text-yellow-500 font-bold text-sm" data-id="element-1116">
              {data.taskTitle || 'Pre-Task Plan'}
            </p>
          </div>
        </div>

        <section data-id="element-1117">
          <div className="flex items-center gap-2 mb-3" data-id="element-1118">
            <ListChecksIcon className="w-5 h-5 text-gray-500" data-id="element-1119" />
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1120">
              MOP Steps — Hazards & Controls
            </h3>
          </div>
          <div className="space-y-3" data-id="element-1121">
            {data.steps.map((step, i) => <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-1122">
                <div className="px-4 py-3 border-b border-gray-200 flex gap-3" data-id="element-1123">
                  <span className="text-yellow-500 font-bold text-sm flex-shrink-0" data-id="element-1124">
                    {i + 1}.
                  </span>
                  <span className="text-gray-900 font-medium text-sm" data-id="element-1125">
                    {step.description}
                  </span>
                </div>
                <div className="px-4 py-3 space-y-3" data-id="element-1126">
                  {step.hazards.map((h, j) => <div key={j} className="space-y-2" data-id="element-1127">
                      <div className="flex items-start gap-2" data-id="element-1128">
                        <AlertTriangleIcon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${h.isNew ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-1129" />
                        <div className="flex-1 flex items-center gap-2 flex-wrap" data-id="element-1130">
                          <span className={`text-xs ${h.isNew ? 'text-yellow-600 font-medium' : 'text-red-600'}`} data-id="element-1131">
                            {h.hazard}
                          </span>
                          {h.riskLevel && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${h.riskLevel === 'HIGH' ? 'bg-red-100 text-red-500' : h.riskLevel === 'MEDIUM' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`} data-id="element-1132">
                              {h.riskLevel}
                            </span>}
                          {h.isNew && <span className="text-[9px] uppercase bg-yellow-100 text-yellow-600 px-1.5 py-0.5 rounded font-bold" data-id="element-1133">
                              New
                            </span>}
                        </div>
                      </div>
                      <div className="ml-5 space-y-1" data-id="element-1134">
                        {h.controls.map((c, k) => <div key={k} className="flex items-start gap-2 text-xs" data-id="element-1135">
                            <CheckCircleIcon className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" data-id="element-1136" />
                            <span className="text-green-700" data-id="element-1137">{c}</span>
                          </div>)}
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>
        </section>

        <section data-id="element-1138">
          <div className="flex items-center gap-2 mb-3" data-id="element-1139">
            <UsersIcon className="w-5 h-5 text-gray-500" data-id="element-1140" />
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1141">
              Crew
            </h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1142">
            {data.crewRoles.map((role, i) => <div key={i} className="bg-gray-100 border border-gray-200 text-gray-700 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-1143">
                {role.name} · {role.role}
              </div>)}
          </div>
        </section>

        <section data-id="element-1144">
          <div className="flex items-center gap-2 mb-3" data-id="element-1145">
            <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-1146" />
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1147">
              Required PPE
            </h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1148">
            {data.ppeControls.map((ppe, i) => <div key={i} className="bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-1149">
                {ppe}
              </div>)}
          </div>
        </section>

        <section data-id="element-1150">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-1151">
            Equipment
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-1152">
            {data.equipment.map((item, i) => <div key={i} className="bg-gray-100 border border-gray-200 text-gray-700 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-1153">
                {item}
              </div>)}
          </div>
        </section>

        <section data-id="element-1154">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-1155">
            Pre-Work Safety Checks
          </h3>
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2" data-id="element-1156">
            {data.safetyChecks.map((check, i) => <div key={i} className="flex items-center gap-2 text-gray-700 text-sm" data-id="element-1157">
                <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-1158" />
                {check}
              </div>)}
          </div>
        </section>

        {data.meetingAdditions && data.meetingAdditions.length > 0 && <section data-id="element-1159">
            <div className="flex items-center gap-2 mb-3" data-id="element-1160">
              <PlusCircleIcon className="w-5 h-5 text-blue-600" data-id="element-1161" />
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1162">
                Meeting Additions
              </h3>
            </div>
            <div className="space-y-3" data-id="element-1163">
              {data.meetingAdditions.map((item, i) => {
            const config = typeConfig[item.type];
            const Icon = config.icon;
            return <div key={i} className={`bg-white border border-gray-200 rounded-xl p-4 border-l-4 ${config.border}`} data-id="element-1164">
                    <div className="flex items-center gap-2 mb-1" data-id="element-1165">
                      <Icon className={`w-3.5 h-3.5 ${config.color}`} data-id="element-1166" />
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1167">
                        {config.label}
                      </span>
                      <span className="text-gray-400 text-[10px]" data-id="element-1168">·</span>
                      <span className="text-[10px] text-gray-400" data-id="element-1169">
                        {item.stepRef}
                      </span>
                    </div>
                    <p className="text-gray-900 font-medium text-sm mb-2" data-id="element-1170">
                      {item.title}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-2.5" data-id="element-1171">
                      <div className="flex items-start gap-2" data-id="element-1172">
                        <UserIcon className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1173" />
                        <div data-id="element-1174">
                          <p className="text-gray-500 text-xs leading-snug italic" data-id="element-1175">
                            "{item.reason}"
                          </p>
                          <p className="text-gray-400 text-[10px] mt-1" data-id="element-1176">
                            — {item.source}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>;
          })}
            </div>
          </section>}

        <section data-id="element-1177">
          <div className="flex items-center gap-2 mb-3" data-id="element-1178">
            <SparklesIcon className="w-5 h-5 text-yellow-500" data-id="element-1179" />
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-1180">
              AI Meeting Notes
            </h3>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-2" data-id="element-1181">
            <p className="text-sm text-yellow-700" data-id="element-1182">
              <span className="font-bold text-gray-900" data-id="element-1183">Meeting Score:</span>{' '}
              {data.meetingScore || 72} / 100
            </p>
            <p className="text-sm text-yellow-700" data-id="element-1184">
              <span className="font-bold text-gray-900" data-id="element-1185">Main Gap:</span> crew
              participation and missing review of wind and emergency response
            </p>
          </div>
        </section>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-1186">
        <PrimaryButton onClick={onNext} data-id="element-1187">Continue to Quiz</PrimaryButton>
        <div className="flex gap-2" data-id="element-1188">
          <SecondaryButton onClick={() => onExport?.()} icon={<DownloadIcon className="w-5 h-5" data-id="element-1190" />} className="flex-1" data-id="element-1189">
            Export PTP
          </SecondaryButton>
          <SecondaryButton onClick={onBack} icon={<ClockIcon className="w-5 h-5" data-id="element-1192" />} className="flex-1" data-id="element-1191">
            Done for Now
          </SecondaryButton>
        </div>
      </div>
    </div>;
}